import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getConsentHandle,
    generateConsentHandle,
    submitConsent,
    rejectConsent,
    submitSelfConsent,
} from "../../services/api/consent.api";
import {
    ConsentHandleResponse,
    ConsentSubmitResponse,
} from "../../services/api/api.types";
import { MOCK_CONSENT_HANDLE } from "../../services/api/mockData";
import { API_DEFAULTS } from "../../services/api/config";

import type { RootState } from "../../app/store";

const USE_MOCKS = process.env.REACT_APP_USE_MOCKS === "true";

export const generateConsentHandleThunk = createAsyncThunk<
    string,
    { mobileNumber: string },
    { rejectValue: string }
>("consent/generate", async ({ mobileNumber }, { rejectWithValue }) => {
    try {
        const pseudouuid = () => {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
                return v.toString(16);
            });
        };

        const payload = {
            ver: "2.0.0",
            timestamp: new Date().toISOString(),
            txnid: pseudouuid(),
            ConsentDetail: {
                consentStart: new Date().toISOString(),
                consentExpiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                consentMode: "STORE",
                fetchType: "ONETIME",
                consentTypes: ["PROFILE", "SUMMARY", "TRANSACTIONS"],
                fiTypes: ["DEPOSIT", "TERM_DEPOSIT", "EQUITIES", "MUTUAL_FUNDS"],
                DataConsumer: {
                    id: "DEV-FIU"
                },
                Customer: {
                    id: `${mobileNumber}@camsfinserv`
                },
                Purpose: {
                    code: "101",
                    refUri: "https://api.rebit.org.in/aa/purpose/101.xml",
                    text: "Wealth management service",
                    Category: {
                        type: "string"
                    }
                },
                FIDataRange: {
                    from: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
                    to: new Date().toISOString()
                },
                DataLife: {
                    unit: "MONTH",
                    value: 6
                },
                Frequency: {
                    unit: "HOUR",
                    value: 1
                },
                DataFilter: [
                    {
                        type: "TRANSACTIONAMOUNT",
                        operator: ">=",
                        value: "10"
                    }
                ]
            }
        };

        const res = await generateConsentHandle(payload);
        if (res.ConsentHandle) {
            return res.ConsentHandle;
        } else {
            return rejectWithValue("Invalid API response generating handle");
        }
    } catch (err: any) {
        return rejectWithValue("Failed to generate dev simulation consent handle");
    }
});

export const getConsentHandleThunk = createAsyncThunk<
    ConsentHandleResponse,
    void,
    { rejectValue: string }
>("consent/handle", async (_, { rejectWithValue, getState }) => {
    if (USE_MOCKS) return MOCK_CONSENT_HANDLE as any;
    try {
        const state = getState() as RootState;
        const sessionId = state.auth.sessionId;
        const mobileNumber = state.auth.mobileNumber;

        const consentHandle = state.auth.consentHandle || state.consent.consentHandle;

        if (!consentHandle) {
            return rejectWithValue("Consent handle is missing. Please restart the journey from the AA redirect link.");
        }

        const res = await getConsentHandle({
            I_MOBILENUMBER: mobileNumber || "",
            I_SESSION: sessionId || "",
            I_ConsentHandle: consentHandle,
            I_USERID: mobileNumber || "",
            I_FIUID: API_DEFAULTS.FIUID,
        });

        if (res.RESULT_CODE !== "200") {
            return rejectWithValue(res.MESSAGE || "Failed to get consent handle details");
        }
        return res;
    } catch (err: any) {
        const state = getState() as RootState;
        const consentHandle = state.auth.consentHandle || state.consent.consentHandle;
        if (consentHandle?.startsWith("DEV_HANDLE_")) {
            console.warn("API failed for dev handle, falling back to mock details");
            return MOCK_CONSENT_HANDLE as any;
        }
        return rejectWithValue(err.message || "Failed to get consent handle details");
    }
});

export const submitConsentThunk = createAsyncThunk<
    ConsentSubmitResponse,
    { consentHandle: string },
    { rejectValue: string; state: RootState }
>("consent/submit", async (payload, { rejectWithValue, getState }) => {
    if (USE_MOCKS) return {} as any;
    try {
        const state = getState();
        const sessionId = state.auth.sessionId || "";
        const mobileNumber = state.auth.mobileNumber || "";
        const userId = state.auth.mobileNumber || "";

        const consentHandle = payload.consentHandle || state.auth.consentHandle || state.consent.consentHandle;
        if (!consentHandle) {
            return rejectWithValue("Consent handle is missing. Please restart the journey.");
        }

        const fiuid = API_DEFAULTS.FIUID;

        const formatCustomDate = (dateString?: string) => {
            const date = dateString ? new Date(dateString) : new Date();
            const MM = String(date.getMonth() + 1).padStart(2, '0');
            const DD = String(date.getDate()).padStart(2, '0');
            const YYYY = date.getFullYear();
            let hours = date.getHours();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const hh = String(hours).padStart(2, '0');
            const mm = String(date.getMinutes()).padStart(2, '0');
            const ss = String(date.getSeconds()).padStart(2, '0');

            return `${MM}/${DD}/${YYYY} ${hh}:${mm}:${ss} ${ampm}`;
        };

        const linkedAccounts = state.account.linkedAccounts;
        const sessionLinkRefNumber = state.account.linkRefNumber || "";
        const selectedFipName = state.fip.selectedFip?.FIP_NAME || "Unknown Bank";
        const selectedFipId = state.fip.selectedFip?.FIP_ID || "CAMC";

        const fipDetailsList = linkedAccounts.map(acc => ({
            CUSTID: acc.CUSTID || mobileNumber,
            FIPID: acc.FIPID || selectedFipId,
            FIPACCREFNUM: sessionLinkRefNumber || acc.FIPACCREFNUM || acc.ACC_ID,
            LINKEDDATE: formatCustomDate(acc.LINKEDDATE),
            FIPACCTYPE: acc.ACC_TYPE,
            FIPACCNUM: acc.MASKED_ACC_NO,
            FITYPE: acc.FI_TYPE,
            CONSENTDATE: formatCustomDate(acc.CONSENTDATE),
            FIPNAME: acc.FIPNAME || selectedFipName,
            FIPACCLINKREF: acc.FIPACCLINKREF || sessionLinkRefNumber || state.auth.uuid || ""
        }));

        const requestPayload = {
            I_MOBILENUMBER: mobileNumber,
            I_ConsentHandle: consentHandle,
            FIPDetailsList: fipDetailsList,
            I_FIUID: fiuid,
            I_SESSION: sessionId,
            I_USERID: userId
        };

        const res = await submitConsent(requestPayload);

        if (res.RESULT_CODE !== "200") {
            return rejectWithValue(res.MESSAGE || "Consent submission failed");
        }
        return res;
    } catch (err: any) {
        return rejectWithValue(err.message || "Consent submission failed");
    }
});

export const rejectConsentThunk = createAsyncThunk<
    void,
    { consentHandle: string; reasonCode: string },
    { rejectValue: string; state: RootState }
>("consent/reject", async (payload, { rejectWithValue, getState }) => {
    if (USE_MOCKS) return;
    try {
        const sessionId = getState().auth.sessionId || "";
        const res = await rejectConsent({
            I_CONSENT_HANDLE: payload.consentHandle,
            I_REASON_CODE: payload.reasonCode,
            I_SESSION_ID: sessionId,
        });
        if (res.RESULT_CODE !== "200") {
            return rejectWithValue(res.RESULT_MESSAGE || "Consent rejection failed");
        }
    } catch {
        return rejectWithValue("Consent rejection failed");
    }
});



export const submitSelfConsentThunk = createAsyncThunk<
    any,
    void,
    { rejectValue: string; state: RootState }
>("consent/submitSelfConsent", async (_, { rejectWithValue, getState }) => {
    try {
        const state = getState();
        const sessionId = state.auth.sessionId || "";
        const mobileNumber = state.auth.mobileNumber || "";
        const userId = state.auth.mobileNumber || "";

        const linkedAccounts = state.account.linkedAccounts;
        const selectedFip = state.fip.selectedFip;

        if (!linkedAccounts.length || !selectedFip) {
            return rejectWithValue("Missing linked accounts or FIP selection.");
        }

        const fipDetailsList = linkedAccounts.map(acc => ({
            FITYPE: acc.FI_TYPE || "DEPOSIT",
            FIPACCLINKREF: acc.FIPACCLINKREF || state.account.linkRefNumber || "",
            FIPACCNUM: acc.MASKED_ACC_NO || "",
            FIPACCREFNUM: acc.FIPACCREFNUM || acc.ACC_ID || "",
            FIPACCTYPE: acc.ACC_TYPE || "SAVINGS",
            FIPID: acc.FIPID || selectedFip.FIP_ID,
            FIPName: acc.FIPNAME || selectedFip.FIP_NAME,
            FIPTYPE: acc.FI_TYPE || "DEPOSIT",
            logo: selectedFip.LOGO_URL || "",
        }));

        const deviceId = state.auth.deviceId || "web-device-id";

        // Self Consent payload requires dates and frequency
        const consentStart = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        const consentExpiry = expiryDate.toISOString().split("T")[0];

        const payload = {
            consentExpiry,
            consentStart,
            I_MOBILE_DEVICEID: deviceId,
            FIPDetailsList: fipDetailsList,
            Frequency: { unit: "Hour", value: "20" },
            I_FETCHPERIOD: "365",
            I_SESSION: sessionId,
            I_MOBILENUMBER: mobileNumber,
            UUID: state.auth.uuid || "",
            I_USERID: userId,
        };

        const res = await submitSelfConsent(payload);

        if (res.RESULT_CODE !== "200") {
            return rejectWithValue(res.MESSAGE || "Self Consent submission failed");
        }
        return res;
    } catch (err: any) {
        return rejectWithValue(err.message || "Self Consent submission failed");
    }
});
